import React, { useMemo, useState } from 'react';
import { Header } from './components/sharktank/Header';
import { KPITiles } from './components/sharktank/KPITiles';
import { MyPipelineTiles } from './components/sharktank/MyPipelineTiles';
import { TabSwitcher, AppTab } from './components/sharktank/TabSwitcher';
import { MyClaims } from './components/sharktank/MyClaims';
import { FilterBar, emptyAdvancedFilters, AdvancedFilters, SortKey, ViewKey } from './components/sharktank/FilterBar';
import { LoanQueue } from './components/sharktank/LoanQueue';
import { Leaderboard } from './components/sharktank/Leaderboard';
import { LoanDetailModal, DetailTab } from './components/sharktank/LoanDetailModal';
import { LogCallModal } from './components/sharktank/LogCallModal';
import { AdminSettingsModal } from './components/sharktank/AdminSettingsModal';
import { mockLoans, mockActivities, mockLeaderboard, mockMyClaims } from './components/sharktank/mockData';
import { Loan, ClaimedLoan, LeaderboardEntry, CallOutcome, LoanNote, PointsConfig, defaultPointsConfig } from './components/sharktank/types';
import { Toaster, toast } from 'sonner';
const toastStyle = {
  background: '#020617',
  border: '1px solid #1e293b',
  color: '#f8fafc'
};
const urgencyRank: Record<string, number> = {
  Critical: 0,
  High: 1,
  Medium: 2
};
const DAILY_CLAIM_CAP = 5;
export function App() {
  const [loans, setLoans] = useState<Loan[]>(mockLoans);
  const [myClaims, setMyClaims] = useState<ClaimedLoan[]>(mockMyClaims);
  const [activities, setActivities] = useState(mockActivities);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>(mockLeaderboard);
  const [tab, setTab] = useState<AppTab>('claims');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeView, setActiveView] = useState<ViewKey>('all');
  const [sortKey, setSortKey] = useState<SortKey>('urgency');
  const [advanced, setAdvanced] = useState<AdvancedFilters>(emptyAdvancedFilters);
  const [detailLoanId, setDetailLoanId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('overview');
  const [logCallLoan, setLogCallLoan] = useState<Loan | null>(null);
  const [pointsConfig, setPointsConfig] = useState<PointsConfig>(defaultPointsConfig);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const loOptions = useMemo(() => Array.from(new Set(loans.map(l => l.assignedLO))), [loans]);
  // Resolve the live detail loan from current state so notes update live
  const detailLoan = useMemo<Loan | null>(() => {
    if (!detailLoanId) return null;
    return myClaims.find(c => c.id === detailLoanId) ?? loans.find(l => l.id === detailLoanId) ?? null;
  }, [detailLoanId, loans, myClaims]);
  const filteredLoans = useMemo(() => {
    let list = [...loans];
    switch (activeView) {
      case 'critical':
        list = list.filter(l => l.timeSinceLastCall > 72);
        break;
      case 'dial':
        list = list.filter(l => l.isDialReady);
        break;
      case 'mine':
        list = list.filter(l => l.assignedLO === 'You' || l.assignedLO.startsWith('Mike'));
        break;
      case 'highvalue':
        list = list.filter(l => l.amount > 500000);
        break;
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(l => l.borrowerName.toLowerCase().includes(q) || l.assignedLO.toLowerCase().includes(q) || l.state.toLowerCase().includes(q) || l.stage.toLowerCase().includes(q));
    }
    if (advanced.stages.length) list = list.filter(l => advanced.stages.includes(l.stage));
    if (advanced.states.length) list = list.filter(l => advanced.states.includes(l.state));
    if (advanced.assignedLOs.length) list = list.filter(l => advanced.assignedLOs.includes(l.assignedLO));
    if (advanced.minAmount != null) list = list.filter(l => l.amount >= advanced.minAmount!);
    if (advanced.maxAmount != null) list = list.filter(l => l.amount <= advanced.maxAmount!);
    if (advanced.minHoursStale != null) list = list.filter(l => l.timeSinceLastCall >= advanced.minHoursStale!);
    list.sort((a, b) => {
      switch (sortKey) {
        case 'urgency':
          return urgencyRank[a.urgency] - urgencyRank[b.urgency];
        case 'stale':
          return b.timeSinceLastCall - a.timeSinceLastCall;
        case 'amount':
          return b.amount - a.amount;
        case 'recent':
          return new Date(b.enteredSharkTankAt).getTime() - new Date(a.enteredSharkTankAt).getTime();
        case 'stage':
          return b.timeInStage - a.timeInStage;
      }
    });
    return list;
  }, [loans, activeView, searchQuery, advanced, sortKey]);
  const recentFundings = useMemo(() => activities.filter(a => a.action === 'Funded'), [activities]);
  const sortedLeaderboard = useMemo(() => [...leaderboard].sort((a, b) => b.points - a.points), [leaderboard]);
  const me = sortedLeaderboard.find(e => e.loName === 'You');
  const myRank = sortedLeaderboard.findIndex(e => e.loName === 'You') + 1 || null;
  const updateLeaderboard = (loName: string, patch: (e: LeaderboardEntry) => LeaderboardEntry) => {
    setLeaderboard(prev => prev.map(e => e.loName === loName ? patch(e) : e));
  };
  const pendingClaimCount = myClaims.filter(c => c.status === 'pending').length;
  // Count claims made by "You" in the rolling 24h window
  const claimsToday = useMemo(() => {
    const since = Date.now() - 1000 * 60 * 60 * 24;
    return activities.filter(a => a.action === 'Claimed' && a.loName === 'You' && new Date(a.timestamp).getTime() >= since).length;
  }, [activities]);
  const claimsRemaining = Math.max(0, DAILY_CLAIM_CAP - claimsToday);
  const atClaimCap = claimsRemaining === 0;
  // Total claimed today across the team (for the team KPI)
  const teamClaimedToday = useMemo(() => {
    const since = Date.now() - 1000 * 60 * 60 * 24;
    return activities.filter(a => a.action === 'Claimed' && new Date(a.timestamp).getTime() >= since).length;
  }, [activities]);
  const handleClaim = (id: string) => {
    const loanToClaim = loans.find(l => l.id === id);
    if (!loanToClaim) return;
    if (atClaimCap) {
      toast.error('Daily claim limit reached', {
        description: `You've already claimed ${DAILY_CLAIM_CAP} loans today. Limit resets in 24h.`,
        style: toastStyle
      });
      return;
    }
    setLoans(prev => prev.filter(l => l.id !== id));
    const claimed: ClaimedLoan = {
      ...loanToClaim,
      assignedLO: 'You',
      claimedAt: new Date().toISOString(),
      status: 'pending'
    };
    setMyClaims(prev => [claimed, ...prev]);
    setActivities(prev => [{
      id: `A-${Date.now()}`,
      loName: 'You',
      borrowerName: loanToClaim.borrowerName,
      action: 'Claimed' as const,
      timestamp: new Date().toISOString(),
      amount: loanToClaim.amount
    }, ...prev].slice(0, 12));
    updateLeaderboard('You', e => ({
      ...e,
      pendingCount: e.pendingCount + 1
    }));
    toast.success(`Claimed ${loanToClaim.borrowerName}'s loan`, {
      description: 'Now in My Claims — points award when funded.',
      style: toastStyle,
      action: {
        label: 'View My Claims',
        onClick: () => setTab('claims')
      }
    });
  };
  const handleRelease = (id: string) => {
    const claim = myClaims.find(c => c.id === id);
    if (!claim) return;
    if (claim.status === 'pending') {
      const {
        claimedAt,
        status,
        fundedAt,
        ...rest
      } = claim;
      const back: Loan = {
        ...rest,
        assignedLO: 'Unassigned'
      };
      setLoans(prev => [back, ...prev]);
      updateLeaderboard('You', e => ({
        ...e,
        pendingCount: Math.max(0, e.pendingCount - 1)
      }));
    }
    setMyClaims(prev => prev.filter(c => c.id !== id));
    setActivities(prev => [{
      id: `A-${Date.now()}`,
      loName: 'You',
      borrowerName: claim.borrowerName,
      action: 'Released' as const,
      timestamp: new Date().toISOString(),
      amount: claim.amount
    }, ...prev].slice(0, 12));
    toast(`Released ${claim.borrowerName} back to the Tank`, {
      style: toastStyle
    });
  };
  const applyCallOutcome = (id: string, outcome: CallOutcome) => {
    const now = new Date().toISOString();
    setLoans(prev => prev.map(l => l.id === id ? {
      ...l,
      callAttempts: l.callAttempts + 1,
      timeSinceLastCall: 0,
      lastCallOutcome: outcome,
      lastCallAt: now
    } : l));
    setMyClaims(prev => prev.map(c => c.id === id ? {
      ...c,
      callAttempts: c.callAttempts + 1,
      timeSinceLastCall: 0,
      lastCallOutcome: outcome,
      lastCallAt: now
    } : c));
  };
  const handleLogCall = (loan: Loan, outcome: CallOutcome) => {
    setLogCallLoan(null);
    if (outcome === 'Connected') {
      const isClaim = myClaims.some(c => c.id === loan.id);
      applyCallOutcome(loan.id, outcome);
      if (!isClaim) {
        if (atClaimCap) {
          // Log the call but skip the auto-claim
          setActivities(prev => [{
            id: `A-${Date.now()}`,
            loName: 'You',
            borrowerName: loan.borrowerName,
            action: 'Called' as const,
            timestamp: new Date().toISOString(),
            amount: loan.amount
          }, ...prev].slice(0, 12));
          toast.error('Connected, but daily claim cap reached', {
            description: 'Call was logged. Limit resets in 24h.',
            style: toastStyle
          });
        } else {
          handleClaim(loan.id);
        }
      } else {
        toast.success(`Connected with ${loan.borrowerName}`, {
          style: toastStyle
        });
      }
      return;
    }
    applyCallOutcome(loan.id, outcome);
    setActivities(prev => [{
      id: `A-${Date.now()}`,
      loName: 'You',
      borrowerName: loan.borrowerName,
      action: 'Called' as const,
      timestamp: new Date().toISOString(),
      amount: loan.amount
    }, ...prev].slice(0, 12));
    toast(`Logged: ${outcome} — ${loan.borrowerName}`, {
      style: toastStyle
    });
  };
  const handleAddNote = (loanId: string, body: string) => {
    const note: LoanNote = {
      id: `N-${Date.now()}`,
      author: 'You',
      body,
      timestamp: new Date().toISOString()
    };
    setLoans(prev => prev.map(l => l.id === loanId ? {
      ...l,
      notes: [...l.notes, note]
    } : l));
    setMyClaims(prev => prev.map(c => c.id === loanId ? {
      ...c,
      notes: [...c.notes, note]
    } : c));
    toast.success('Note added', {
      style: toastStyle
    });
  };
  const handleClickToDial = (loan: Loan) => {
    toast(`Calling ${loan.borrowerName}...`, {
      description: 'Connecting via CTI (placeholder)',
      style: toastStyle
    });
  };
  const openDetail = (loan: Loan, t: DetailTab) => {
    setDetailTab(t);
    setDetailLoanId(loan.id);
  };
  return <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-orange-500/30" data-id="element-0">
      <Toaster position="top-right" theme="dark" data-id="element-1" />

      <Header searchQuery={searchQuery} onSearchChange={setSearchQuery} onOpenAdmin={() => setIsAdminOpen(true)} data-id="element-2" />

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8" data-id="element-3">
        <TabSwitcher active={tab} onChange={setTab} myClaimsCount={myClaims.length} pendingCount={pendingClaimCount} tankCount={loans.length} claimsRemaining={claimsRemaining} dailyCap={DAILY_CLAIM_CAP} data-id="element-4" />

        {tab === 'claims' ? <MyPipelineTiles claims={myClaims} me={me} rank={myRank} totalPlayers={sortedLeaderboard.length} data-id="element-5" /> : <KPITiles loans={loans} claimedToday={teamClaimedToday} data-id="element-6" />}

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8" data-id="element-7">
          <div className="xl:col-span-3 space-y-6" data-id="element-8">
            {tab === 'claims' ? <MyClaims claims={myClaims} onRelease={handleRelease} onLogCall={c => setLogCallLoan(c)} onViewLoan={c => openDetail(c, 'overview')} onViewTimeline={c => openDetail(c, 'timeline')} onViewNotes={c => openDetail(c, 'notes')} onDial={handleClickToDial} onJumpToTank={() => setTab('tank')} data-id="element-9" /> : <>
                <FilterBar activeView={activeView} onViewChange={setActiveView} sortKey={sortKey} onSortChange={setSortKey} advanced={advanced} onAdvancedChange={setAdvanced} loOptions={loOptions} data-id="element-10" />
                <LoanQueue loans={filteredLoans} onClaim={handleClaim} onViewLoan={l => openDetail(l, 'overview')} onViewTimeline={l => openDetail(l, 'timeline')} onViewNotes={l => openDetail(l, 'notes')} onLogCall={l => setLogCallLoan(l)} onDialOne={handleClickToDial} claimsRemaining={claimsRemaining} dailyCap={DAILY_CLAIM_CAP} data-id="element-11" />
              </>}
          </div>

          <div className="xl:col-span-1 space-y-6" data-id="element-12">
            <div className="sticky top-24" data-id="element-13">
              <Leaderboard entries={leaderboard} recentFundings={recentFundings} currentUser="You" pointsConfig={pointsConfig} data-id="element-14" />
            </div>
          </div>
        </div>
      </main>

      <LoanDetailModal loan={detailLoan} initialTab={detailTab} onClose={() => setDetailLoanId(null)} onAddNote={handleAddNote} data-id="element-15" />

      <LogCallModal loan={logCallLoan} onClose={() => setLogCallLoan(null)} onLog={handleLogCall} data-id="element-16" />

      <AdminSettingsModal open={isAdminOpen} config={pointsConfig} onClose={() => setIsAdminOpen(false)} onSave={setPointsConfig} data-id="element-17" />
    </div>;
}